import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BACKEND_URL } from '../config';
import {
  CaretRight, CaretLeft, CheckCircle, CircleNotch, WarningCircle,
  Globe, Shield, Lightning, FlowArrow, ArrowRight, Cube, X,
  MagnifyingGlass, Check, Info, Heartbeat, PaperPlaneTilt, File
} from '@phosphor-icons/react';

const STEPS = [
  { key: 'basics', label: 'Basic Details', icon: Cube },
  { key: 'endpoint', label: 'Endpoint & Auth', icon: Globe },
  { key: 'capabilities', label: 'Capabilities', icon: Lightning },
  { key: 'contract', label: 'Input / Output', icon: FlowArrow },
  { key: 'validate', label: 'Validation', icon: Shield },
  { key: 'review', label: 'Review & Submit', icon: PaperPlaneTilt },
];

const AUTH_METHODS = [
  { value: 'none', label: 'None', desc: 'No authentication required' },
  { value: 'api_key', label: 'API Key', desc: 'Pass an API key in header' },
  { value: 'bearer_token', label: 'Bearer Token', desc: 'OAuth2 bearer token' },
  { value: 'hmac_signature', label: 'HMAC Signature', desc: 'Request signature verification' },
];

// ─── Step Progress Bar ──────────────────────────────────────
const StepProgress = ({ currentStep, steps }) => (
  <div className="flex items-center gap-1 px-1 py-3" data-testid="wizard-progress">
    {steps.map((step, i) => {
      const Icon = step.icon;
      const isActive = i === currentStep;
      const isDone = i < currentStep;
      return (
        <React.Fragment key={step.key}>
          <div className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium transition-all ${
            isActive ? 'bg-[var(--brand-primary)] text-white' :
            isDone ? 'bg-emerald-500/15 text-emerald-400' :
            'text-[var(--text-muted)] bg-[var(--surface-2)]'
          }`}>
            {isDone ? <Check size={12} weight="bold" /> : <Icon size={12} />}
            <span className="hidden sm:inline">{step.label}</span>
            <span className="sm:hidden">{i + 1}</span>
          </div>
          {i < steps.length - 1 && (
            <CaretRight size={10} className={isDone ? 'text-emerald-400' : 'text-[var(--text-muted)]'} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ─── Step 1: Basic Details ──────────────────────────────────
const StepBasics = ({ data, onChange }) => (
  <div className="space-y-4" data-testid="wizard-step-basics">
    <div>
      <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Agent Name *</label>
      <input
        type="text"
        value={data.name}
        onChange={e => onChange({ name: e.target.value })}
        placeholder="e.g. Document Classifier API"
        className="w-full px-3 py-2 rounded-lg bg-[var(--surface-2)] border border-[var(--border-default)] text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--brand-primary)] focus:outline-none"
        data-testid="wizard-input-name"
      />
    </div>
    <div>
      <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Description</label>
      <textarea
        value={data.description}
        onChange={e => onChange({ description: e.target.value })}
        placeholder="What does this agent do? Be specific."
        rows={3}
        className="w-full px-3 py-2 rounded-lg bg-[var(--surface-2)] border border-[var(--border-default)] text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--brand-primary)] focus:outline-none resize-none"
        data-testid="wizard-input-description"
      />
    </div>
    <div>
      <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Developer / Company Name</label>
      <input
        type="text"
        value={data.developer_name}
        onChange={e => onChange({ developer_name: e.target.value })}
        placeholder="e.g. Acme Corp"
        className="w-full px-3 py-2 rounded-lg bg-[var(--surface-2)] border border-[var(--border-default)] text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--brand-primary)] focus:outline-none"
        data-testid="wizard-input-developer"
      />
    </div>
    <div>
      <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Category / Use Case (optional)</label>
      <input
        type="text"
        value={(data.tags || []).join(', ')}
        onChange={e => onChange({ tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
        placeholder="e.g. nlp, document-processing, finance"
        className="w-full px-3 py-2 rounded-lg bg-[var(--surface-2)] border border-[var(--border-default)] text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--brand-primary)] focus:outline-none"
        data-testid="wizard-input-tags"
      />
      <p className="text-[10px] text-[var(--text-muted)] mt-1">Comma-separated tags</p>
    </div>
  </div>
);

// ─── Step 2: Endpoint & Auth ────────────────────────────────
const StepEndpoint = ({ data, onChange }) => (
  <div className="space-y-4" data-testid="wizard-step-endpoint">
    <div>
      <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Endpoint URL *</label>
      <input
        type="url"
        value={data.endpoint_url}
        onChange={e => onChange({ endpoint_url: e.target.value })}
        placeholder="https://api.example.com/v1/invoke"
        className="w-full px-3 py-2 rounded-lg bg-[var(--surface-2)] border border-[var(--border-default)] text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--brand-primary)] focus:outline-none font-mono"
        data-testid="wizard-input-endpoint"
      />
      <p className="text-[10px] text-[var(--text-muted)] mt-1">The POST endpoint the platform will call to invoke your agent</p>
    </div>

    <div>
      <label className="block text-xs font-medium text-[var(--text-secondary)] mb-2">Auth Method</label>
      <div className="grid grid-cols-2 gap-2">
        {AUTH_METHODS.map(m => (
          <button
            key={m.value}
            onClick={() => onChange({ auth_method: m.value, auth_config: {} })}
            className={`p-2.5 rounded-lg border text-left transition-all ${
              data.auth_method === m.value
                ? 'border-[var(--brand-primary)] bg-[var(--brand-primary)]/10'
                : 'border-[var(--border-default)] bg-[var(--surface-2)] hover:border-[var(--border-hover)]'
            }`}
            data-testid={`wizard-auth-${m.value}`}
          >
            <span className={`text-xs font-medium ${data.auth_method === m.value ? 'text-[var(--brand-primary)]' : 'text-[var(--text-primary)]'}`}>{m.label}</span>
            <p className="text-[10px] text-[var(--text-muted)] mt-0.5">{m.desc}</p>
          </button>
        ))}
      </div>
    </div>

    {data.auth_method === 'api_key' && (
      <div>
        <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">API Key *</label>
        <input
          type="password"
          value={data.auth_config?.api_key || ''}
          onChange={e => onChange({ auth_config: { ...data.auth_config, api_key: e.target.value } })}
          placeholder="sk-..."
          className="w-full px-3 py-2 rounded-lg bg-[var(--surface-2)] border border-[var(--border-default)] text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--brand-primary)] focus:outline-none font-mono"
          data-testid="wizard-input-apikey"
        />
      </div>
    )}

    {data.auth_method === 'bearer_token' && (
      <div>
        <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Bearer Token *</label>
        <input
          type="password"
          value={data.auth_config?.token || ''}
          onChange={e => onChange({ auth_config: { ...data.auth_config, token: e.target.value } })}
          placeholder="eyJhbG..."
          className="w-full px-3 py-2 rounded-lg bg-[var(--surface-2)] border border-[var(--border-default)] text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--brand-primary)] focus:outline-none font-mono"
          data-testid="wizard-input-token"
        />
      </div>
    )}

    {data.auth_method === 'hmac_signature' && (
      <div>
        <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">HMAC Secret *</label>
        <input
          type="password"
          value={data.auth_config?.secret || ''}
          onChange={e => onChange({ auth_config: { ...data.auth_config, secret: e.target.value } })}
          placeholder="your-hmac-secret"
          className="w-full px-3 py-2 rounded-lg bg-[var(--surface-2)] border border-[var(--border-default)] text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--brand-primary)] focus:outline-none font-mono"
          data-testid="wizard-input-hmac"
        />
      </div>
    )}

    <div>
      <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Health Check URL (optional)</label>
      <input
        type="url"
        value={data.health_url}
        onChange={e => onChange({ health_url: e.target.value })}
        placeholder="https://api.example.com/health"
        className="w-full px-3 py-2 rounded-lg bg-[var(--surface-2)] border border-[var(--border-default)] text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--brand-primary)] focus:outline-none font-mono"
        data-testid="wizard-input-healthurl"
      />
    </div>
  </div>
);

// ─── Step 3: Capabilities ───────────────────────────────────
const StepCapabilities = ({ data, onChange }) => {
  const [capabilities, setCapabilities] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCaps = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/capabilities`, { credentials: 'include' });
        if (res.ok) {
          const list = await res.json();
          setCapabilities(Array.isArray(list) ? list : list.capabilities || []);
        }
      } catch (e) {
        console.error('Failed to fetch capabilities:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchCaps();
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return capabilities;
    const q = search.toLowerCase();
    return capabilities.filter(c =>
      c.name?.toLowerCase().includes(q) ||
      c.capability_id?.toLowerCase().includes(q) ||
      c.category?.toLowerCase().includes(q) ||
      c.description?.toLowerCase().includes(q)
    );
  }, [capabilities, search]);

  const selected = data.declared_capabilities || [];

  const toggle = (capId) => {
    const next = selected.includes(capId)
      ? selected.filter(c => c !== capId)
      : [...selected, capId];
    onChange({ declared_capabilities: next });
  };

  // Group by category
  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach(c => {
      const cat = c.category || 'Uncategorized';
      if (!map[cat]) map[cat] = [];
      map[cat].push(c);
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="wizard-step-capabilities">
        <CircleNotch size={24} className="animate-spin text-[var(--brand-primary)]" />
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="wizard-step-capabilities">
      <div className="relative">
        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search capabilities..."
          className="w-full pl-8 pr-3 py-2 rounded-lg bg-[var(--surface-2)] border border-[var(--border-default)] text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--brand-primary)] focus:outline-none"
          data-testid="wizard-cap-search"
        />
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map(id => {
            const cap = capabilities.find(c => c.capability_id === id);
            return (
              <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[var(--brand-primary)]/15 text-[var(--brand-primary)] text-xs font-medium">
                {cap?.name || id}
                <button onClick={() => toggle(id)} className="hover:text-white"><X size={10} /></button>
              </span>
            );
          })}
        </div>
      )}

      <div className="max-h-[280px] overflow-y-auto space-y-3 pr-1">
        {grouped.map(([category, caps]) => (
          <div key={category}>
            <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-1">{category}</p>
            <div className="space-y-1">
              {caps.map(cap => {
                const isSelected = selected.includes(cap.capability_id);
                return (
                  <button
                    key={cap.capability_id}
                    onClick={() => toggle(cap.capability_id)}
                    className={`w-full text-left px-3 py-2 rounded-lg border transition-all ${
                      isSelected
                        ? 'border-[var(--brand-primary)] bg-[var(--brand-primary)]/8'
                        : 'border-transparent bg-[var(--surface-2)] hover:bg-[var(--surface-3)]'
                    }`}
                    data-testid={`wizard-cap-${cap.capability_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-medium ${isSelected ? 'text-[var(--brand-primary)]' : 'text-[var(--text-primary)]'}`}>
                        {cap.name}
                      </span>
                      {isSelected && <Check size={14} weight="bold" className="text-[var(--brand-primary)]" />}
                    </div>
                    {cap.description && (
                      <p className="text-[10px] text-[var(--text-muted)] mt-0.5 line-clamp-1">{cap.description}</p>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {grouped.length === 0 && (
          <p className="text-sm text-[var(--text-muted)] text-center py-6">No capabilities found</p>
        )}
      </div>
    </div>
  );
};

// ─── Step 4: Input/Output Contract ──────────────────────────
const StepContract = ({ data, onChange }) => {
  const [inputMode, setInputMode] = useState('json');
  const [outputMode, setOutputMode] = useState('json');

  const formatJson = (obj) => {
    try {
      return typeof obj === 'string' ? obj : JSON.stringify(obj, null, 2);
    } catch {
      return '';
    }
  };

  const parseJson = (str) => {
    try {
      return JSON.parse(str);
    } catch {
      return null;
    }
  };

  return (
    <div className="space-y-4" data-testid="wizard-step-contract">
      <div className="flex items-center gap-2 p-2.5 rounded-lg bg-blue-500/8 border border-blue-500/20">
        <Info size={14} className="text-blue-400 flex-shrink-0" />
        <p className="text-[11px] text-blue-300">
          Schemas are optional but recommended. They help the platform validate data flowing to and from your agent.
        </p>
      </div>

      <div>
        <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Input Schema (JSON)</label>
        <textarea
          value={formatJson(data.input_schema)}
          onChange={e => {
            const parsed = parseJson(e.target.value);
            if (parsed) onChange({ input_schema: parsed });
          }}
          placeholder={'{\n  "type": "object",\n  "properties": {\n    "text": { "type": "string" }\n  }\n}'}
          rows={6}
          className="w-full px-3 py-2 rounded-lg bg-[var(--surface-2)] border border-[var(--border-default)] text-xs text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--brand-primary)] focus:outline-none resize-none font-mono"
          data-testid="wizard-input-schema"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Output Schema (JSON)</label>
        <textarea
          value={formatJson(data.output_schema)}
          onChange={e => {
            const parsed = parseJson(e.target.value);
            if (parsed) onChange({ output_schema: parsed });
          }}
          placeholder={'{\n  "type": "object",\n  "properties": {\n    "result": { "type": "string" }\n  }\n}'}
          rows={6}
          className="w-full px-3 py-2 rounded-lg bg-[var(--surface-2)] border border-[var(--border-default)] text-xs text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--brand-primary)] focus:outline-none resize-none font-mono"
          data-testid="wizard-output-schema"
        />
      </div>
    </div>
  );
};

// ─── Step 5: Validation ─────────────────────────────────────
const StepValidate = ({ data, onValidationComplete }) => {
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ran, setRan] = useState(false);

  const runValidation = useCallback(async (withHealth = false) => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/federation/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          endpoint_url: data.endpoint_url,
          auth_method: data.auth_method,
          auth_config: data.auth_config,
          declared_capabilities: data.declared_capabilities,
          input_schema: data.input_schema,
          output_schema: data.output_schema,
          check_health: withHealth,
        }),
      });
      if (res.ok) {
        const result = await res.json();
        setChecks(result.checks || []);
        onValidationComplete(result);
      }
    } catch (e) {
      setChecks([{ field: 'error', label: 'Validation', passed: false, message: 'Failed to run validation' }]);
    } finally {
      setLoading(false);
      setRan(true);
    }
  }, [data, onValidationComplete]);

  useEffect(() => {
    if (!ran) runValidation(false);
  }, []);

  return (
    <div className="space-y-4" data-testid="wizard-step-validate">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[var(--text-secondary)]">Pre-submission validation checks</p>
        <div className="flex gap-2">
          <button
            onClick={() => runValidation(false)}
            disabled={loading}
            className="px-3 py-1.5 rounded-lg bg-[var(--surface-3)] text-xs font-medium text-[var(--text-primary)] hover:bg-[var(--surface-4)] transition-colors disabled:opacity-50"
            data-testid="wizard-revalidate"
          >
            {loading ? <CircleNotch size={12} className="animate-spin" /> : 'Re-check'}
          </button>
          <button
            onClick={() => runValidation(true)}
            disabled={loading}
            className="px-3 py-1.5 rounded-lg bg-emerald-500/15 text-xs font-medium text-emerald-400 hover:bg-emerald-500/25 transition-colors disabled:opacity-50 flex items-center gap-1"
            data-testid="wizard-health-check"
          >
            <Heartbeat size={12} /> Test Endpoint
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {checks.map((check, i) => (
          <div
            key={i}
            className={`flex items-start gap-3 p-3 rounded-lg border ${
              check.passed
                ? 'border-emerald-500/20 bg-emerald-500/5'
                : 'border-red-500/20 bg-red-500/5'
            }`}
            data-testid={`wizard-check-${check.field}`}
          >
            {check.passed
              ? <CheckCircle size={16} weight="fill" className="text-emerald-400 mt-0.5 flex-shrink-0" />
              : <WarningCircle size={16} weight="fill" className="text-red-400 mt-0.5 flex-shrink-0" />
            }
            <div className="flex-1 min-w-0">
              <p className={`text-xs font-medium ${check.passed ? 'text-emerald-300' : 'text-red-300'}`}>
                {check.label}
              </p>
              <p className="text-[10px] text-[var(--text-muted)] mt-0.5">{check.message}</p>
              {check.warnings?.length > 0 && (
                <div className="mt-1 space-y-0.5">
                  {check.warnings.map((w, j) => (
                    <p key={j} className="text-[10px] text-amber-400">- {w}</p>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {checks.length === 0 && !loading && (
          <p className="text-sm text-[var(--text-muted)] text-center py-8">Click check to validate</p>
        )}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <CircleNotch size={20} className="animate-spin text-[var(--brand-primary)]" />
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Step 6: Review & Submit ────────────────────────────────
const StepReview = ({ data, validationResult }) => {
  const authLabel = AUTH_METHODS.find(m => m.value === data.auth_method)?.label || data.auth_method;
  const capsCount = (data.declared_capabilities || []).length;

  return (
    <div className="space-y-3" data-testid="wizard-step-review">
      <div className="rounded-lg bg-[var(--surface-2)] border border-[var(--border-default)] divide-y divide-[var(--border-subtle)]">
        <div className="px-4 py-3">
          <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">Agent Name</p>
          <p className="text-sm font-medium text-[var(--text-primary)] mt-0.5">{data.name || '—'}</p>
        </div>
        {data.description && (
          <div className="px-4 py-3">
            <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">Description</p>
            <p className="text-xs text-[var(--text-secondary)] mt-0.5">{data.description}</p>
          </div>
        )}
        <div className="px-4 py-3">
          <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">Endpoint</p>
          <p className="text-xs font-mono text-[var(--text-primary)] mt-0.5 break-all">{data.endpoint_url || '—'}</p>
        </div>
        <div className="px-4 py-3 flex gap-8">
          <div>
            <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">Auth</p>
            <p className="text-xs text-[var(--text-primary)] mt-0.5">{authLabel}</p>
          </div>
          <div>
            <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">Capabilities</p>
            <p className="text-xs text-[var(--text-primary)] mt-0.5">{capsCount} selected</p>
          </div>
        </div>
        {(data.declared_capabilities || []).length > 0 && (
          <div className="px-4 py-3">
            <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-1">Selected Capabilities</p>
            <div className="flex flex-wrap gap-1">
              {data.declared_capabilities.map(c => (
                <span key={c} className="px-2 py-0.5 rounded-md bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] text-[10px] font-medium">{c}</span>
              ))}
            </div>
          </div>
        )}
        {data.developer_name && (
          <div className="px-4 py-3">
            <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">Developer</p>
            <p className="text-xs text-[var(--text-primary)] mt-0.5">{data.developer_name}</p>
          </div>
        )}
      </div>

      {validationResult && (
        <div className={`flex items-center gap-2 p-2.5 rounded-lg ${
          validationResult.valid
            ? 'bg-emerald-500/8 border border-emerald-500/20'
            : 'bg-amber-500/8 border border-amber-500/20'
        }`}>
          {validationResult.valid
            ? <CheckCircle size={14} weight="fill" className="text-emerald-400" />
            : <WarningCircle size={14} weight="fill" className="text-amber-400" />
          }
          <p className={`text-[11px] ${validationResult.valid ? 'text-emerald-300' : 'text-amber-300'}`}>
            {validationResult.summary}
          </p>
        </div>
      )}

      <div className="flex items-center gap-2 p-2.5 rounded-lg bg-blue-500/8 border border-blue-500/20">
        <Info size={14} className="text-blue-400 flex-shrink-0" />
        <p className="text-[11px] text-blue-300">
          Submitting will send this agent for admin review. You can also save as draft and submit later from My Agents.
        </p>
      </div>
    </div>
  );
};


// ═════════════════════════════════════════════════════════════
// MAIN WIZARD COMPONENT
// ═════════════════════════════════════════════════════════════
const FederationWizard = ({ onClose, onComplete, initialData }) => {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [error, setError] = useState('');
  const [savedFedId, setSavedFedId] = useState(initialData?.federation_id || null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    developer_name: '',
    endpoint_url: '',
    auth_method: 'none',
    auth_config: {},
    declared_capabilities: [],
    input_schema: {},
    output_schema: {},
    health_url: '',
    tags: [],
    ...(initialData || {}),
  });

  const updateData = useCallback((partial) => {
    setFormData(prev => ({ ...prev, ...partial }));
    setError('');
  }, []);

  // Step validation
  const canProceed = useMemo(() => {
    switch (step) {
      case 0: return formData.name.trim().length > 0;
      case 1: return formData.endpoint_url.trim().length > 0 && /^https?:\/\/.+/.test(formData.endpoint_url);
      case 2: return formData.declared_capabilities.length > 0;
      case 3: return true; // schemas optional
      case 4: return true; // validation step
      case 5: return true; // review step
      default: return false;
    }
  }, [step, formData]);

  const next = () => {
    if (step < STEPS.length - 1 && canProceed) setStep(step + 1);
  };

  const back = () => {
    if (step > 0) setStep(step - 1);
  };

  // Save draft
  const saveDraft = useCallback(async () => {
    setSaving(true);
    setError('');
    try {
      const url = savedFedId
        ? `${BACKEND_URL}/api/federation/submissions/${savedFedId}`
        : `${BACKEND_URL}/api/federation/submissions`;
      const method = savedFedId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...formData, submit_now: false }),
      });

      if (res.ok) {
        const result = await res.json();
        const newId = result.federation?.federation_id || savedFedId;
        if (newId) setSavedFedId(newId);
        return { success: true, federation_id: newId };
      } else {
        const err = await res.json().catch(() => ({}));
        const errMsg = typeof err.detail === 'string' 
          ? err.detail 
          : (err.detail?.error || err.detail?.message || JSON.stringify(err.detail) || 'Failed to save draft');
        setError(errMsg);
        return { success: false };
      }
    } catch (e) {
      setError('Network error — please try again');
      return { success: false };
    } finally {
      setSaving(false);
    }
  }, [formData, savedFedId]);

  // Submit for review
  const submitForReview = useCallback(async () => {
    setSubmitting(true);
    setError('');
    try {
      // First save
      const saveResult = await saveDraft();
      if (!saveResult.success) {
        setSubmitting(false);
        return;
      }

      const fedId = saveResult.federation_id || savedFedId;
      if (!fedId) {
        setError('No draft ID — please save first');
        setSubmitting(false);
        return;
      }

      // Then submit
      const res = await fetch(`${BACKEND_URL}/api/federation/submissions/${fedId}/submit`, {
        method: 'POST',
        credentials: 'include',
      });

      if (res.ok) {
        onComplete?.({ federation_id: fedId, action: 'submitted' });
      } else {
        const err = await res.json().catch(() => ({}));
        const errMsg = typeof err.detail === 'string' 
          ? err.detail 
          : (err.detail?.error || err.detail?.message || JSON.stringify(err.detail) || 'Failed to submit');
        setError(errMsg);
      }
    } catch (e) {
      setError('Network error — please try again');
    } finally {
      setSubmitting(false);
    }
  }, [saveDraft, savedFedId, onComplete]);

  const handleSaveDraft = async () => {
    const result = await saveDraft();
    if (result.success) {
      onComplete?.({ federation_id: result.federation_id, action: 'draft' });
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="federation-wizard">
      {/* Header */}
      <div className="px-4 py-3 border-b border-[var(--border-subtle)] flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-emerald-500/15 flex items-center justify-center">
            <Globe size={14} className="text-emerald-400" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-[var(--text-primary)]">Register External Agent</h2>
            <p className="text-[10px] text-[var(--text-muted)]">Step {step + 1} of {STEPS.length}</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg hover:bg-[var(--surface-3)] transition-colors"
          data-testid="wizard-close"
        >
          <X size={16} className="text-[var(--text-muted)]" />
        </button>
      </div>

      {/* Progress */}
      <div className="px-3 border-b border-[var(--border-subtle)]">
        <StepProgress currentStep={step} steps={STEPS} />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {step === 0 && <StepBasics data={formData} onChange={updateData} />}
        {step === 1 && <StepEndpoint data={formData} onChange={updateData} />}
        {step === 2 && <StepCapabilities data={formData} onChange={updateData} />}
        {step === 3 && <StepContract data={formData} onChange={updateData} />}
        {step === 4 && <StepValidate data={formData} onValidationComplete={setValidationResult} />}
        {step === 5 && <StepReview data={formData} validationResult={validationResult} />}
      </div>

      {/* Error */}
      {error && (
        <div className="px-4 py-2 bg-red-500/10 border-t border-red-500/20">
          <p className="text-xs text-red-400">{error}</p>
        </div>
      )}

      {/* Footer / Actions */}
      <div className="px-4 py-3 border-t border-[var(--border-subtle)] flex items-center justify-between shrink-0">
        <button
          onClick={back}
          disabled={step === 0}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-[var(--text-secondary)] hover:bg-[var(--surface-3)] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid="wizard-back"
        >
          <CaretLeft size={12} /> Back
        </button>

        <div className="flex items-center gap-2">
          {/* Save Draft — available from step 1 onward */}
          {step >= 1 && (
            <button
              onClick={handleSaveDraft}
              disabled={saving || !formData.name.trim()}
              className="px-3 py-1.5 rounded-lg text-xs font-medium text-[var(--text-secondary)] border border-[var(--border-default)] hover:bg-[var(--surface-3)] transition-colors disabled:opacity-40 flex items-center gap-1"
              data-testid="wizard-save-draft"
            >
              {saving ? <CircleNotch size={12} className="animate-spin" /> : <File size={12} />}
              Save Draft
            </button>
          )}

          {/* Next / Submit */}
          {step < STEPS.length - 1 ? (
            <button
              onClick={next}
              disabled={!canProceed}
              className="flex items-center gap-1 px-4 py-1.5 rounded-lg text-xs font-semibold bg-[var(--brand-primary)] text-white hover:brightness-110 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="wizard-next"
            >
              Next <CaretRight size={12} />
            </button>
          ) : (
            <button
              onClick={submitForReview}
              disabled={submitting || !formData.name.trim() || !formData.endpoint_url.trim()}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-500 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="wizard-submit"
            >
              {submitting ? <CircleNotch size={12} className="animate-spin" /> : <PaperPlaneTilt size={12} />}
              Submit for Review
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FederationWizard;
